import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { AuthContext } from "../context/AuthContext";
import {
  getUreticiBakicilari,
  getBakiciInfo,
  getUlkeKodlari,
  deleteBakici,
  getBakiciAtananKumesler,
  deleteBakiciKumesAtama,
} from "../services/api";
import BakiciFormModal from "../components/BakiciFormModal";
import BakiciAtamaModal from "../components/BakiciAtamaModal";
import BakiciDetayModal from "../components/BakiciDetayModal";

const orangeColor = "#FF7A2B";
const orangeHoverColor = "#F57C00";

const BakicilarimPage = () => {
  const { user, token } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [bakicilar, setBakicilar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedBakici, setSelectedBakici] = useState(null);
  const [ureticiId, setUreticiId] = useState(null);
  const [ulkeler, setUlkeler] = useState([]);

  const [atamaModalOpen, setAtamaModalOpen] = useState(false);
  const [selectedAtamaBakici, setSelectedAtamaBakici] = useState(null);
  const [bakiciKumesMap, setBakiciKumesMap] = useState({});
  const [detayModalOpen, setDetayModalOpen] = useState(false);
  const [selectedDetayBakici, setSelectedDetayBakici] = useState(null);

  // Üretici ID'yi al
  const fetchUreticiId = useCallback(async () => {
    if (!token) return;

    try {
      const response = await getBakiciInfo();
      let bakiciBilgisi = response.data?.data || response.data || {};

      if (Array.isArray(bakiciBilgisi) && bakiciBilgisi.length > 0) {
        bakiciBilgisi = bakiciBilgisi[0];
      }

      const fetchedUreticiId = bakiciBilgisi?.URETICI_ID || null;
      setUreticiId(fetchedUreticiId);
      return fetchedUreticiId;
    } catch (error) {
      enqueueSnackbar(
        "Üretici bilgileri alınırken hata oluştu: " + error.message,
        { variant: "error" }
      );
      return null;
    }
  }, [token, enqueueSnackbar]);

  const fetchAllBakiciKumesler = useCallback(async (bakiciListesi) => {
    const kumesMap = {};

    await Promise.all(
      bakiciListesi.map(async (bakici) => {
        try {
          const bakiciKullaniciId = bakici.BAKICI_KULLANICI_ID;

          if (!bakiciKullaniciId) {
            // console.warn("Bakıcı Kullanıcı ID bulunamadı:", bakici);
            kumesMap[bakici.BAKICI_KULLANICI_ID] = [];
            return;
          }

          const response = await getBakiciAtananKumesler(bakiciKullaniciId);
          const kumesData = response.data?.data || response.data || [];

          // ✅ Debug için console'a yazdır
          // console.log(
          //   `🔍 ${bakici.BAKICI_ADI} ${bakici.BAKICI_SOYADI} kümeleri:`,
          //   kumesData
          // );

          kumesMap[bakici.BAKICI_KULLANICI_ID] = Array.isArray(kumesData)
            ? kumesData
            : [];
        } catch (error) {
          // console.error(
          //   `Bakıcı ${bakici.BAKICI_KULLANICI_ID} kümes verisi alınamadı:`,
          //   error
          // );
          kumesMap[bakici.BAKICI_KULLANICI_ID] = [];
        }
      })
    );

    // console.log("🔍 Tüm bakıcı-kümes map:", kumesMap);
    setBakiciKumesMap(kumesMap);
  }, []);

  // Bakıcı listesini getir
  const fetchBakicilar = useCallback(
    async (uId = null) => {
      const targetUreticiId = uId || ureticiId;

      if (!token || !targetUreticiId) return;

      setLoading(true);
      try {
        const response = await getUreticiBakicilari(targetUreticiId);
        const bakiciListesi = response.data?.data || response.data || [];

        // console.log("=== BAKICI LİSTESİ DEBUG ==="); // ← Ekle
        // console.log("İlk bakıcı:", bakiciListesi[0]); // ← Ekle
        // console.log(
        //   "BAKICI_KULLANICI_ID var mı?:",
        //   bakiciListesi[0]?.BAKICI_KULLANICI_ID
        // ); // ← Ekle

        setBakicilar(Array.isArray(bakiciListesi) ? bakiciListesi : []);

        if (Array.isArray(bakiciListesi) && bakiciListesi.length > 0) {
          fetchAllBakiciKumesler(bakiciListesi);
        }
      } catch (error) {
        enqueueSnackbar(
          "Bakıcı listesi yüklenirken hata oluştu: " + error.message,
          { variant: "error" }
        );
      } finally {
        setLoading(false);
      }
    },
    [token, ureticiId, enqueueSnackbar, fetchAllBakiciKumesler]
  );

  // Ülke listesini yükle
  const fetchUlkeler = useCallback(async () => {
    try {
      const response = await getUlkeKodlari();
      const ulkeData = response.data?.data || response.data || [];
      setUlkeler(Array.isArray(ulkeData) ? ulkeData : []);
    } catch (error) {
      // console.error("Ülke listesi yükleme hatası:", error);
    }
  }, []);

  // İlk yükleme - Sadece token değişince çalışsın
  useEffect(() => {
    const initData = async () => {
      await fetchUlkeler();
      const fetchedUreticiId = await fetchUreticiId();
      if (fetchedUreticiId) {
        fetchBakicilar(fetchedUreticiId);
      }
    };

    if (token) {
      initData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // ← SADECE token

  // Bakıcı ekle
  const handleAddBakici = () => {
    setSelectedBakici(null);
    setFormModalOpen(true);
  };

  // Bakıcı düzenle
  const handleEditBakici = (bakici) => {
    setSelectedBakici(bakici);
    setFormModalOpen(true);
    setDetayModalOpen(false);
  };

  // Bakıcı sil
  const handleDeleteBakici = async (bakici) => {
    if (!ureticiId) {
      enqueueSnackbar("Üretici bilgisi alınamadı", { variant: "error" });
      return;
    }

    const confirmed = window.confirm(
      `${bakici.BAKICI_ADI} ${bakici.BAKICI_SOYADI} isimli bakıcıyı silmek istediğinize emin misiniz?`
    );

    if (!confirmed) return;

    try {
      await deleteBakici(bakici.BAKICI_KULLANICI_ID, ureticiId);
      enqueueSnackbar("Bakıcı başarıyla silindi!", { variant: "success" });
      fetchBakicilar(ureticiId);
      setDetayModalOpen(false);
    } catch (error) {
      const errorMessage =
        error.response?.data?.data ||
        error.response?.data?.data ||
        error.message ||
        "Bakıcı silinirken hata oluştu";

      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  // Bakıcı atama
  const handleAssignBakici = (bakici) => {
    setSelectedAtamaBakici(bakici);
    setAtamaModalOpen(true);
    setDetayModalOpen(false);
  };

  // Detay göster
  const handleShowDetay = (bakici) => {
    setSelectedDetayBakici(bakici);
    setDetayModalOpen(true);
  };

  // Tarih formatlama
  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      if (
        typeof dateString === "object" &&
        dateString.year &&
        dateString.month &&
        dateString.day
      ) {
        const day = String(dateString.day).padStart(2, "0");
        const month = String(dateString.month).padStart(2, "0");
        const year = dateString.year;
        return `${day}.${month}.${year}`;
      }

      const dateStr = String(dateString);

      if (dateStr.includes(".")) {
        return dateStr;
      }

      if (dateStr.includes("T") || dateStr.includes("-")) {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      }

      return dateStr;
    } catch (error) {
      return "-";
    }
  };

  // Evlilik durumu
  const getEvlilikText = (evli) => {
    return evli === "E" ? "Evli" : evli === "H" ? "Bekar" : "-";
  };

  // Ülke adı bulma
  const getUlkeAdi = (ulkeId) => {
    if (!ulkeId) return "-";
    const ulke = ulkeler.find((u) => u.ULKE_ID === ulkeId);
    return ulke?.ULKEADI || "-";
  };

  // ✅ Kümes atamasını sil
  const handleDeleteKumesAtama = async (bakici, kumesAtamaId, kumesAdi) => {
    if (!kumesAtamaId) {
      enqueueSnackbar("Atama ID bulunamadı", { variant: "warning" });
      return;
    }

    const confirmed = window.confirm(
      `${bakici.BAKICI_ADI} ${bakici.BAKICI_SOYADI} adlı bakıcının "${kumesAdi}" kümesinden çıkarılmasını onaylıyor musunuz?`
    );

    if (!confirmed) return;

    try {
      //console.log("🗑️ Kümes ataması siliniyor:", kumesAtamaId);

      await deleteBakiciKumesAtama(kumesAtamaId);

      enqueueSnackbar(
        `${bakici.BAKICI_ADI} ${bakici.BAKICI_SOYADI} adlı bakıcı "${kumesAdi}" kümesinden çıkarıldı`,
        { variant: "success" }
      );

      // Listeyi yenile
      fetchBakicilar(ureticiId);
    } catch (error) {
      // console.error("❌ Kümes ataması silinirken hata:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.data ||
        error.message ||
        "Kümes ataması silinemedi";

      enqueueSnackbar(`Hata: ${errorMessage}`, { variant: "error" });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 4 }}>
        {/* Başlık ve Ekle Butonu */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: orangeColor, fontWeight: "bold" }}
          >
            BAKICILARIM ({bakicilar.length})
          </Typography>
          <Button
            variant="contained"
            onClick={handleAddBakici}
            sx={{
              backgroundColor: orangeColor,
              "&:hover": { backgroundColor: orangeHoverColor },
              fontSize: "1.1rem",
              py: 1.5,
              px: 4,
            }}
          >
            + YENİ BAKICI EKLE
          </Button>
        </Box>

        {/* Tablo */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress sx={{ color: orangeColor }} size={60} />
          </Box>
        ) : bakicilar.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
              Henüz bakıcı eklenmemiş
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Yeni bakıcı eklemek için yukarıdaki butona tıklayın
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: "1.1rem", py: 2 }}
                  >
                    AD SOYAD
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: "1.1rem", py: 2 }}
                  >
                    TELEFON
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: "1.1rem", py: 2 }}
                  >
                    DOĞUM TARİHİ
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: "1.1rem", py: 2 }}
                  >
                    UYRUK
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: "1.1rem", py: 2 }}
                  >
                    ATANAN KÜMESLER
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: "1.1rem", py: 2 }}
                    align="center"
                  >
                    İŞLEMLER
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bakicilar.map((bakici) => (
                  <TableRow
                    key={bakici.BAKICI_KULLANICI_ID}
                    sx={{
                      "&:hover": { backgroundColor: "#fafafa" },
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  >
                    <TableCell sx={{ fontSize: "1rem", py: 2.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                        {bakici.BAKICI_ADI} {bakici.BAKICI_SOYADI}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", py: 2.5 }}>
                      {bakici.KULLANICI_TEL_NO ?? "TELEFON NUMARASI YOK"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", py: 2.5 }}>
                      {formatDate(bakici.DOGUM_TARIHI)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "1rem", py: 2.5 }}>
                      {getUlkeAdi(bakici.UYRUK_ULKE_ID)}
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      {bakiciKumesMap[bakici.BAKICI_KULLANICI_ID]?.length >
                      0 ? (
                        <Stack direction="column" spacing={1}>
                          {/* ✅ Kümes sayısı chip'i */}
                          <Chip
                            label={`${
                              bakiciKumesMap[bakici.BAKICI_KULLANICI_ID].length
                            } KÜMES`}
                            sx={{
                              backgroundColor: orangeColor,
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "0.95rem",
                              px: 1,
                            }}
                          />

                          {/* ✅ Her kümes için detaylı liste ve silme butonu */}
                          <Stack direction="column" spacing={0.5}>
                            {bakiciKumesMap[bakici.BAKICI_KULLANICI_ID].map(
                              (kumes, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    border: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.9rem",
                                      color: "text.primary",
                                      fontWeight: 500,
                                      flex: 1,
                                    }}
                                  >
                                    {kumes.KUMES_ADI || `Kümes ${index + 1}`}
                                  </Typography>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleDeleteKumesAtama(
                                        bakici,
                                        kumes.KUMES_BAKICI_ES_ID || kumes.ID, // ← API'nin beklediği ID
                                        kumes.KUMES_ADI || `Kümes ${index + 1}`
                                      )
                                    }
                                    disabled={
                                      !kumes.KUMES_BAKICI_ES_ID && !kumes.ID
                                    }
                                    sx={{
                                      minWidth: "auto",
                                      px: 1.5,
                                      py: 0.25,
                                      fontSize: "0.75rem",
                                      color: "#d32f2f",
                                      borderColor: "#d32f2f",
                                      "&:hover": {
                                        backgroundColor: "#ffebee",
                                        borderColor: "#b71c1c",
                                      },
                                      "&:disabled": {
                                        color: "#ccc",
                                        borderColor: "#ccc",
                                      },
                                    }}
                                  >
                                    Çıkar
                                  </Button>
                                </Box>
                              )
                            )}
                          </Stack>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Atama yok
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2.5 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <Button
                          variant="outlined"
                          onClick={() => handleShowDetay(bakici)}
                          sx={{
                            borderColor: "primary.main",
                            color: "primary.main",
                            fontSize: "0.9rem",
                            py: 1,
                            px: 2,
                          }}
                        >
                          DETAY
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleEditBakici(bakici)}
                          sx={{
                            backgroundColor: "primary.main",
                            fontSize: "0.9rem",
                            py: 1,
                            px: 2,
                          }}
                        >
                          DÜZENLE
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleAssignBakici(bakici)}
                          sx={{
                            backgroundColor: orangeColor,
                            "&:hover": { backgroundColor: orangeHoverColor },
                            fontSize: "0.9rem",
                            py: 1,
                            px: 2,
                          }}
                        >
                          KÜMES ATA
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleDeleteBakici(bakici)}
                          color="error"
                          sx={{
                            fontSize: "0.9rem",
                            py: 1,
                            px: 2,
                          }}
                        >
                          SİL
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Bakıcı Ekleme/Düzenleme Modalı */}
      <BakiciFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        bakici={selectedBakici}
        onSuccess={() => {
          setFormModalOpen(false);
          fetchBakicilar();
        }}
      />

      {/* Bakıcı Atama Modalı */}
      <BakiciAtamaModal
        open={atamaModalOpen}
        onClose={() => setAtamaModalOpen(false)}
        bakici={selectedAtamaBakici}
        atananKumesler={
          bakiciKumesMap[selectedAtamaBakici?.BAKICI_KULLANICI_ID] || []
        } // ← Yeni prop
        onSuccess={() => {
          setAtamaModalOpen(false);
          fetchBakicilar();
        }}
      />

      {/* Bakıcı Detay Modalı */}
      <BakiciDetayModal
        open={detayModalOpen}
        onClose={() => setDetayModalOpen(false)}
        bakici={selectedDetayBakici}
        atananKumesler={
          bakiciKumesMap[selectedDetayBakici?.BAKICI_KULLANICI_ID] || []
        }
        onEdit={() => handleEditBakici(selectedDetayBakici)}
        onDelete={() => handleDeleteBakici(selectedDetayBakici)}
        onAssign={() => handleAssignBakici(selectedDetayBakici)}
        formatDate={formatDate}
        getUlkeAdi={getUlkeAdi}
        getEvlilikText={getEvlilikText}
      />
    </Box>
  );
};

export default BakicilarimPage;
